import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import pb from '@/lib/pocketbase'
import type { Pimpinan } from '@/types/pocketbase'
import Card from '@/components/ui/Card'
import Badge from '@/components/ui/Badge'
import Button from '@/components/ui/Button'
import EmptyState from '@/components/ui/EmptyState'
import Input from '@/components/ui/Input'

export default function PimpinanPage() {
  const [search, setSearch] = useState('')

  const { data: pimpinanResult, isLoading } = useQuery({
    queryKey: ['pimpinan', search],
    queryFn: () =>
      pb.collection('pimpinan').getList(1, 100, {
        filter: search ? `nama ~ "${search}" || nbm ~ "${search}"` : '',
        expand: 'daerah_id,jabatan_id,periode_id',
        sort: 'nama',
      }),
  })

  const items = pimpinanResult?.items || []

  return (
    <div className="py-4 space-y-4">
      <div className="space-y-3">
        <Input
          placeholder="Cari nama atau NBM..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          leftIcon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          }
        />
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-card h-20 animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          title="Belum ada pimpinan"
          description="Data pimpinan akan muncul di sini."
          action={{
            label: '+ Tambah Pimpinan',
            onClick: () => {},
          }}
        />
      ) : (
        items.map((p) => (
          <Card key={p.id}>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                {p.foto ? (
                  <img
                    src={pb.files.getUrl(p, (Array.isArray(p.foto) ? p.foto[0] : p.foto) as string, { thumb: '100x100' })}
                    alt={p.nama}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <span className="text-sm font-bold text-primary">
                    {(p.nama || '?').slice(0, 2).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-bold text-text-primary truncate">{p.nama}</p>
                  {p.is_active && <Badge variant="green">Aktif</Badge>}
                </div>
                {p.nbm && <p className="text-xs text-text-secondary">NBM: {p.nbm}</p>}
                <div className="flex items-center gap-2 text-xs text-text-secondary mt-0.5">
                  {(p.expand?.daerah_id as { nama?: string })?.nama && (
                    <span>{(p.expand?.daerah_id as { nama?: string }).nama}</span>
                  )}
                  {(p.expand?.jabatan_id as { nama?: string })?.nama && (
                    <>
                      <span>—</span>
                      <span>{(p.expand?.jabatan_id as { nama?: string }).nama}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </Card>
        ))
      )}

      <Button block>+ Tambah Pimpinan</Button>
    </div>
  )
}
